type MutationMethod = "POST" | "PATCH" | "PUT" | "DELETE";

type ExtendedRequestInit = RequestInit & {
  onErrorMessage?: string | ((res: Response) => string | Promise<string>);
  /** Set to true when the endpoint may return 204 No Content. Widens the return type to `T | null`. */
  expects204?: boolean;
  /** Set to true when the endpoint returns a plain-text body instead of JSON. */
  expectsText?: boolean;
  /**
   * Set to true to get the raw (redirect/error-handled) `Response` back instead of a parsed
   * body — e.g. to branch on a response header like `content-type` before deciding how to
   * consume the body. Pass `Response` as the type param: `apiClient<Response>(url, { expectsResponse: true })`.
   */
  expectsResponse?: boolean;
};

/** Error thrown by apiClient/apiMutate with the HTTP status code attached. */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Type guard for narrowing caught errors to ApiError. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Parse the JSON body from an ApiError message, or return null if it isn't valid JSON. */
export function parseApiErrorBody(error: ApiError): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(error.message);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

interface FieldErrorDetail {
  field?: unknown;
  message?: unknown;
}

/**
 * Format a request-validation `details` array (`{ field, message }` entries, as
 * returned by pipeline-service's RequestValidationError handler) into a single
 * string naming which field failed. Returns null when there's nothing usable.
 */
function formatErrorDetails(details: unknown): string | null {
  if (!Array.isArray(details)) return null;
  const parts = details
    .filter((entry): entry is FieldErrorDetail => !!entry && typeof entry === "object")
    .map(({ field, message }) =>
      typeof field === "string" && typeof message === "string" ? `${field}: ${message}` : null
    )
    .filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join("; ") : null;
}

/**
 * Dig a human-readable message out of a parsed access-controller error body.
 * Handles the v1 envelope `{ error: { message } }`, the legacy HTTPException
 * handler that nests that envelope under `message` (so an FGA 403 arrives as
 * `{ message: { error: { message } } }`), and the plain `{ detail }` / `{ message }`
 * strings FastAPI emits otherwise. Returns null when no string message is found.
 * When a `details` array naming the offending field(s) is present alongside the
 * message (as in pipeline-service's request-validation errors), it's appended.
 */
export function extractErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const { detail, message, error, details } = body as Record<string, unknown>;
  if (typeof detail === "string") return detail;
  // Recurse into wrapped envelopes before falling back to a top-level string.
  const nested = extractErrorMessage(error) ?? extractErrorMessage(message);
  if (nested) return nested;
  if (typeof message === "string") {
    const detailSuffix = formatErrorDetails(details);
    return detailSuffix ? `${message} (${detailSuffix})` : message;
  }
  return null;
}

/**
 * Extract a human-readable message from an ApiError.
 * The raw `message` is JSON.stringify'd from the API response body, so we attempt
 * to parse it and pull a message out of whichever error envelope was returned.
 */
export function getApiErrorMessage(error: ApiError): string {
  return extractErrorMessage(parseApiErrorBody(error)) ?? error.message;
}

const defaultErrorMessage = async (res: Response) => {
  let errorMessage = "";
  try {
    const resp = await res.json();
    errorMessage = JSON.stringify(resp);
  } catch {
    const text = await res.text().catch(() => "");
    errorMessage = text || `Request failed with status ${res.status}`;
  }
  return errorMessage;
};

/**
 * Fetch with redirect and error handling shared by apiClient and apiStreamClient.
 * Throws ApiError on non-2xx responses. Returns the raw Response on success so
 * callers can decide how to consume the body.
 */
async function fetchWithRedirects(
  url: string,
  fetchOptions: RequestInit,
  onErrorMessage?: ExtendedRequestInit["onErrorMessage"]
): Promise<Response> {
  const res = await fetch(url, { ...fetchOptions, redirect: "manual" });

  // handle cross origin redirects
  if (res.type === "opaqueredirect") {
    const parsedUrl = new URL(res.url);
    const strippedUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}${
      parsedUrl.port ? `:${parsedUrl.port}` : ""
    }`;
    window.location.href = strippedUrl;
    // Page is being redirected anyway, so return a promise that never resolves.
    return new Promise<Response>(() => {});
  }

  // handle same origin redirects by following them
  if ([301, 302, 307, 308].includes(res.status)) {
    const redirected = await fetch(url, { ...fetchOptions, redirect: "follow" });
    if (!redirected.ok) {
      throw new ApiError(await defaultErrorMessage(redirected), redirected.status);
    }
    return redirected;
  }

  if (!res.ok) {
    let message: string | undefined;
    try {
      message =
        typeof onErrorMessage === "string" ? onErrorMessage : await onErrorMessage?.(res.clone());
    } catch {
      // onErrorMessage failed, fall through to default
    }
    if (!message) {
      message = await defaultErrorMessage(res);
    }
    throw new ApiError(message, res.status);
  }

  return res;
}

export function apiClient<T = unknown>(url: string, options?: ExtendedRequestInit): Promise<T>;
/** Mutation overload — returns `T | null` when the endpoint may return 204 No Content. */
export function apiClient<T = unknown>(
  url: string,
  options: ExtendedRequestInit & { method: MutationMethod; expects204: true }
): Promise<T | null>;
/** Mutation overload — returns `T` (most mutations return a response body). */
export function apiClient<T = unknown>(
  url: string,
  options: ExtendedRequestInit & { method: MutationMethod }
): Promise<T>;

/**
 * Shared API client for making fetch requests with consistent error handling.
 *
 * Features:
 * - Handles cross-origin and same-origin redirects
 * - Parses JSON error responses (with fallback for non-JSON error bodies like HTML 504s)
 * - Handles 204 No Content
 * - Throws errors with `.status` for use with the global error handler
 */
export async function apiClient<T = unknown>(
  url: string,
  options: ExtendedRequestInit = {}
): Promise<T | null> {
  const { onErrorMessage, expects204, expectsText, expectsResponse, ...fetchOptions } = options;
  const res = await fetchWithRedirects(url, fetchOptions, onErrorMessage);

  if (expects204 && res.status === 204) {
    return null;
  } else if (expects204) {
    throw new ApiError("Expected 204 No Content response", res.status);
  } else if (res.status === 204) {
    throw new ApiError("Unexpected 204 No Content response", res.status);
  }

  if (expectsResponse) {
    return res as unknown as T;
  }

  if (expectsText) {
    return (await res.text()) as unknown as T;
  }

  return res.json();
}

/**
 * Convenience wrapper for mutation requests (POST/PATCH/PUT/DELETE).
 * Automatically sets Content-Type and JSON-serializes the body.
 *
 * Use this for mutations in event handlers or standalone API modules where
 * React hooks can't be used. For declarative data fetching in components,
 * prefer `useFetcher` or `useMutator` from ~/hooks/useFetcher.
 *
 * Pass `{ expects204: true }` in options when the endpoint may return 204 No Content
 * to widen the return type to `T | null`.
 */
/** Overload: returns `T | null` when the endpoint may return 204 No Content. */
export function apiMutate<T = unknown>(
  url: string,
  method: MutationMethod,
  body: unknown,
  options: Omit<ExtendedRequestInit, "method" | "body"> & { expects204: true }
): Promise<T | null>;
/** Mutation — returns `T` (most mutations return a response body). */
export function apiMutate<T = unknown>(
  url: string,
  method: MutationMethod,
  body?: unknown,
  options?: Omit<ExtendedRequestInit, "method" | "body">
): Promise<T>;
export async function apiMutate<T = unknown>(
  url: string,
  method: MutationMethod,
  body?: unknown,
  options?: Omit<ExtendedRequestInit, "method" | "body">
): Promise<T | null> {
  const init: ExtendedRequestInit = {
    method,
    ...options,
  };

  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.headers = {
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    };
    init.body = JSON.stringify(body);
  }

  return apiClient<T>(url, init);
}

/**
 * Creates a bound pair of apiClient/apiMutate with shared default options.
 * Per-call options are merged on top, so callers can still override.
 */
export const createApiClient = (defaults: Pick<ExtendedRequestInit, "onErrorMessage">) => ({
  apiClient: <T = unknown>(url: string, options?: ExtendedRequestInit) =>
    apiClient<T>(url, { ...defaults, ...options }),
  apiMutate: <T = unknown>(
    url: string,
    method: "POST" | "PATCH" | "PUT" | "DELETE",
    body?: unknown,
    options?: Omit<ExtendedRequestInit, "method" | "body">
  ) => apiMutate<T>(url, method, body, { ...defaults, ...options }),
});

type StreamRequestInit = Omit<ExtendedRequestInit, "expects204" | "expectsText">;

/**
 * Like apiClient, but returns the raw Response for streaming bodies (e.g. NDJSON).
 *
 * Applies the same redirect handling and ApiError-throwing as apiClient, but does
 * NOT consume the response body — the caller reads it incrementally via readStreamEvents().
 * Use for streaming endpoints only; for regular endpoints, prefer apiClient/apiMutate.
 */
export async function apiStreamClient(
  url: string,
  options: StreamRequestInit = {}
): Promise<Response> {
  const { onErrorMessage, ...fetchOptions } = options;
  if (fetchOptions.body) {
    fetchOptions.headers = { "Content-Type": "application/json", ...fetchOptions.headers };
  }
  return fetchWithRedirects(url, fetchOptions, onErrorMessage);
}
